import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import {
    Search,
    CheckCircle,
    XCircle,
    RefreshCw,
    Megaphone,
    Clock,
    TrendingUp,
    TrendingDown
} from 'lucide-react';

const AdminP2PAdsPage = () => {
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');

    useEffect(() => {
        fetchAds();
    }, [statusFilter, typeFilter]);

    const fetchAds = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (statusFilter) params.append('status', statusFilter);
            if (typeFilter) params.append('type', typeFilter);

            const res = await api.get(`/admin/p2p-ads?${params.toString()}`);
            setAds(res.data.ads || []);
        } catch (error) {
            console.error('Fetch P2P ads error:', error);
            toast.error('Failed to load P2P ads');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            active: 'bg-green-500/20 text-green-400 border-green-500/30',
            offline: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            cancelled: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
        };
        const icons = {
            active: <CheckCircle size={14} />,
            offline: <Clock size={14} />,
            completed: <CheckCircle size={14} />,
            cancelled: <XCircle size={14} />
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.active}`}>
                {icons[status] || icons.active}
                {status?.charAt(0).toUpperCase() + status?.slice(1)}
            </span>
        );
    };

    const getTypeBadge = (type) => {
        return type === 'sell' ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border bg-red-500/20 text-red-400 border-red-500/30">
                <TrendingDown size={14} />
                SELL
            </span>
        ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border bg-green-500/20 text-green-400 border-green-500/30">
                <TrendingUp size={14} />
                BUY
            </span>
        );
    };

    const filteredAds = ads.filter(ad => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            ad.user?.email?.toLowerCase().includes(search) ||
            ad.user?.firstName?.toLowerCase().includes(search) ||
            ad.asset?.toLowerCase().includes(search) ||
            ad.id?.toLowerCase().includes(search)
        );
    });

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />

            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                            <Megaphone className="text-orange-400" /> P2P Advertisements
                        </h1>
                        <p className="text-gray-400 text-sm">View all P2P trading advertisements</p>
                    </div>
                    <button
                        onClick={fetchAds}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
                    >
                        <RefreshCw size={18} /> Refresh
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700">
                <div className="flex flex-wrap gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search by user email, name, or asset..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    >
                        <option value="">All Types</option>
                        <option value="buy">Buy</option>
                        <option value="sell">Sell</option>
                    </select>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    >
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="offline">Offline</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                </div>
            </div>

            {/* Content */}
            <div className="p-6">
                <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                    {loading ? (
                        <div className="text-center py-12 text-gray-400">Loading P2P ads...</div>
                    ) : filteredAds.length === 0 ? (
                        <div className="text-center py-16 text-gray-400">
                            <Megaphone size={48} className="mx-auto mb-4 text-orange-400/50" />
                            <p className="text-lg font-medium">No P2P ads found</p>
                            <p className="text-sm mt-2">Advertisements will appear here when users post them</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-900/50">
                                    <tr className="text-left text-gray-400 text-sm">
                                        <th className="px-4 py-3 font-medium">Ad ID</th>
                                        <th className="px-4 py-3 font-medium">User</th>
                                        <th className="px-4 py-3 font-medium">Type</th>
                                        <th className="px-4 py-3 font-medium">Asset</th>
                                        <th className="px-4 py-3 font-medium">Price</th>
                                        <th className="px-4 py-3 font-medium">Amount</th>
                                        <th className="px-4 py-3 font-medium">Limits</th>
                                        <th className="px-4 py-3 font-medium">Status</th>
                                        <th className="px-4 py-3 font-medium">Created</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {filteredAds.map((ad) => (
                                        <tr key={ad.id} className="hover:bg-slate-700/30 transition">
                                            <td className="px-4 py-3">
                                                <span className="text-blue-400 text-xs font-mono">
                                                    {ad.id?.slice(0, 8)}...
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div>
                                                    <div className="text-white font-medium text-sm">
                                                        {ad.user?.firstName} {ad.user?.lastName}
                                                    </div>
                                                    <div className="text-xs text-gray-400">{ad.user?.email}</div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">{getTypeBadge(ad.type)}</td>
                                            <td className="px-4 py-3">
                                                <span className="px-2 py-1 bg-slate-700 rounded text-white text-sm font-medium">
                                                    {ad.asset}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-white font-mono text-sm">
                                                ₹{parseFloat(ad.price).toFixed(2)}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-white text-sm">
                                                    {parseFloat(ad.remainingAmount).toFixed(4)}
                                                </div>
                                                <div className="text-xs text-gray-400">
                                                    of {parseFloat(ad.totalAmount).toFixed(4)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-gray-300 text-sm">
                                                ₹{parseFloat(ad.minLimit).toFixed(0)} - ₹{parseFloat(ad.maxLimit).toFixed(0)}
                                            </td>
                                            <td className="px-4 py-3">{getStatusBadge(ad.status)}</td>
                                            <td className="px-4 py-3 text-gray-400 text-sm">
                                                {new Date(ad.createdAt).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Stats */}
                <div className="mt-4 text-center text-gray-400 text-sm">
                    Showing {filteredAds.length} advertisements
                </div>
            </div>
        </div>
    );
};

export default AdminP2PAdsPage;
